import React from 'react';
import { useTheme } from '@mui/material';
import { IconButton } from '../IconButton';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { Button } from '../Button';
import { TextInput } from '../TextInput';
import { AbstractButton } from '../AbstractButton';
import { NumberInput } from '../NumberInput';
import { Combobox } from '../Combobox';
import { Popover, PopoverTrigger, PopoverContent } from '../Popover';
import { Card } from '../Card';
import { useDisclosure } from '../../utils/useDisclosure';

export interface RelativeTimeInputProps {
  /** The number value to display in the input */
  selectedNumberValue: number;
  /** The time unit to display in the input */
  selectedTimeUnit: RelativeTimeUnits;
  /** Whether the popover is initially open */
  initialIsOpen?: boolean;
  /** The name of the input */
  nameStart?: string;
  /** The content to display in the end of the input */
  inputEndContent?: React.ReactNode;
  /** The label of the input */
  label?: string;
  /** The function to call when the other options button is clicked */
  onOtherOptions: () => void;
  /** The function to call when the relative time is applied */
  onRelativeTimeApply: (unit: RelativeTimeUnits, numberVal: number) => void;
  /** The function to call when the cancel button is clicked */
  onCancel?: () => void;
  /** A ref for the popover container (for click-outside detection) */
  dateWrapperRef?: React.RefObject<HTMLDivElement>;
}

export enum RelativeTimeUnits {
  Mins = 'mins',
  Hours = 'hours',
  Days = 'days',
  Weeks = 'weeks',
  Months = 'months',
  Years = 'years',
}

export const RelativeTimeInputComponent: React.FC<RelativeTimeInputProps> = ({
  onOtherOptions,
  onRelativeTimeApply,
  selectedNumberValue,
  selectedTimeUnit,
  initialIsOpen = false,
  onCancel,
  inputEndContent,
  label = 'Set a relative time',
  dateWrapperRef,
}) => {
  const muiTheme = useTheme();
  const { tokens, componentTokens } = muiTheme.palette;

  const { isOpen, close, toggle } = useDisclosure({ initialState: initialIsOpen });
  const [numValue, setNumValue] = React.useState(selectedNumberValue);
  const [timeUnit, setTimeUnit] = React.useState(selectedTimeUnit);

  React.useEffect(() => {
    setNumValue(selectedNumberValue);
    setTimeUnit(selectedTimeUnit);
  }, [selectedNumberValue, selectedTimeUnit]);

  const handleCancel = React.useCallback(() => {
    if (onCancel) {
      onCancel();
      close();
      return;
    }

    setNumValue(selectedNumberValue);
    setTimeUnit(selectedTimeUnit);
    close();
  }, [setNumValue, setTimeUnit, selectedNumberValue, selectedTimeUnit, close, onCancel]);

  return (
    <Popover isOpen={isOpen} onChange={() => toggle()}>
      <PopoverTrigger as={Box} position="relative" zIndex={1}>
        <Box>
          <TextInput
            onClick={e => e.preventDefault()}
            onChange={() => {}}
            data-testid="relative-time-picker"
            label=""
            value={`Last ${selectedNumberValue} ${selectedTimeUnit}`}
            sx={{
              '& .MuiInputBase-input': {
                lineHeight: '16px',
              },
            }}
          />
        </Box>
        <Box position="absolute" top={5.5} right={7.5} zIndex={2}>
          {inputEndContent && <>{inputEndContent}</>}
          <IconButton variant="ghost" aria-label="Toggle picker" size="medium" icon="calendar" />
        </Box>
      </PopoverTrigger>
      <PopoverContent alignment="bottom-right" sx={{ mt: 2 }} ref={dateWrapperRef}>
        <Card
          borderWidth={0}
          shadow="dark300"
          sx={{
            backgroundColor: componentTokens.selectMenuItems.color.item.background.containerBase,
          }}
        >
          <Flex data-testid="relative-time-popover" direction="column">
            <Box p={4}>
              <Flex spacing={4} align="center">
                <Box as="span">Last</Box>
                <Box width={120}>
                  <NumberInput
                    label=""
                    value={numValue}
                    onInputNumberChange={x => setNumValue(x)}
                  />
                </Box>
                <Box width={120}>
                  <Combobox<RelativeTimeUnits>
                    id="relative-time-units"
                    hideLabel
                    label={label}
                    items={Object.values(RelativeTimeUnits)}
                    data-testid="relative-time-units"
                    onChange={x => setTimeUnit(x)}
                    placeholder="Choose something"
                    value={timeUnit}
                    showClearSelectionControl={false}
                  />
                </Box>
              </Flex>
            </Box>
            <Flex
              align="center"
              spacing={3}
              justify="center"
              borderTop={`1px solid ${tokens.borderPrimary}`}
              p={3}
              width={491}
            >
              <Flex
                width="100%"
                align="center"
                justifyContent="space-between"
                justify="center"
                spacing={4}
              >
                <AbstractButton
                  color={tokens.actionFocus}
                  fontSize="medium"
                  _selected={{ color: tokens.inputText }}
                  _hover={{ color: tokens.linkProminentHover }}
                  onClick={onOtherOptions}
                  data-testid="other-options"
                >
                  {'<'} Other options
                </AbstractButton>
                <Flex justifyContent="space-between" spacing={4}>
                  <Button onClick={handleCancel} size="medium" variant="secondary">
                    Cancel
                  </Button>
                  <Button
                    size="medium"
                    onClick={() => {
                      onRelativeTimeApply(timeUnit, numValue);
                      close();
                    }}
                  >
                    Apply
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
      </PopoverContent>
    </Popover>
  );
};

export const RelativeTimeInput = React.memo(RelativeTimeInputComponent);
